import { isPrimeFlowEnabled } from '@/features/prime/primeFlow';
import { isPrimeDividaHabilitada } from '@/features/prime/utils/isPrimeDividaHabilitada';
import { calcularChaveDivida } from '@/helpers/dividaKey';
import type { DividaResource } from '@/models/resources';
import { acertoPriorizouExibicaoDividaPrime } from '@/modules/events/acerto';
class DividaPrimeOrdenacaoService {
  private posicaoModeloOrdenacao: number | null = null;
  private eventoDisparado = false;

  /**
   * Identifica quais dívidas de `dividasBase` são Prime elegíveis no escopo informado
   * (hoje 0 ou 1 item: a primeira elegível). Não decide onde essa(s) dívida(s) entram na
   * listagem final — isso é papel de quem prioriza a lista (ex.: DividasSyncService).
   *
   * `chavesPrimeiroLote`:
   * - `Set` com chaves: restringe ao 1º lote (sync em streaming);
   * - `Set` vazio: 1º lote ainda indefinido — não identifica;
   * - `null`: lista já fechada (ex.: reload via cache) — busca em `dividasBase` inteira.
   */
  identificar(dividasBase: DividaResource[], chavesPrimeiroLote: ReadonlySet<string> | null): DividaResource[] {
    if (!isPrimeFlowEnabled()) {
      this.limparIdentificacao();
      return [];
    }

    if (chavesPrimeiroLote?.size === 0) {
      this.limparIdentificacao();
      return [];
    }

    const indiceDividaPrime = dividasBase.findIndex(divida =>
      DividaPrimeOrdenacaoService.isElegivelParaPriorizacaoPrime(divida, chavesPrimeiroLote),
    );

    if (indiceDividaPrime < 0) {
      this.limparIdentificacao();
      return [];
    }

    const dividaPriorizada = dividasBase[indiceDividaPrime];
    this.posicaoModeloOrdenacao = indiceDividaPrime + 1;

    return [dividaPriorizada];
  }

  /**
   * Dispara o evento de exibição quando a Prime efetivamente ficou no topo da lista final.
   * Deve ser chamado após montar a listagem priorizada, não em `identificar`.
   */
  dispararEventoExibicaoSeNecessario(listaOrdenada: readonly DividaResource[]): void {
    if (this.eventoDisparado) {
      return;
    }

    const primeiraDivida = listaOrdenada[0];
    const posicaoModeloOrdenacao = this.posicaoModeloOrdenacao;

    const temDestaquePrime =
      isPrimeFlowEnabled() &&
      primeiraDivida != null &&
      isPrimeDividaHabilitada(primeiraDivida);

    if (!temDestaquePrime || posicaoModeloOrdenacao == null) {
      return;
    }

    this.eventoDisparado = true;
    acertoPriorizouExibicaoDividaPrime({
      dividaId: primeiraDivida.id,
      codigoCredor: primeiraDivida.codigoCredor,
      credor: primeiraDivida.interface,
      posicaoModeloOrdenacao,
    });
  }

  reset(): void {
    this.limparIdentificacao();
    this.eventoDisparado = false;
  }

  private limparIdentificacao(): void {
    this.posicaoModeloOrdenacao = null;
  }

  private static isElegivelParaPriorizacaoPrime(
    divida: DividaResource,
    chavesPrimeiroLote: ReadonlySet<string> | null,
  ): boolean {
    if (!isPrimeDividaHabilitada(divida)) {
      return false;
    }

    if (chavesPrimeiroLote === null) {
      return true;
    }

    return chavesPrimeiroLote.has(calcularChaveDivida(divida.api, divida.id));
  }
}

export const dividaPrimeOrdenacaoService = new DividaPrimeOrdenacaoService();

export default DividaPrimeOrdenacaoService;
